import * as Api from '@ViewModels';
import { Button } from '@HoudiniUI/Button';
import { Checkbox } from '@HoudiniUI/Checkbox';
import { Input } from '@HoudiniUI/Input';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
	useDeleteSharedMailboxMutation,
	useSharedMailboxQuery,
	useTestSharedMailboxMutation,
	useUpdateSharedMailboxMutation,
} from '../../../../../../queries/User/useSharedMailbox';
import { baseStyleSheet } from '../../../../../styles/styles';
import { styleSheet } from './styles';

interface IProps {
	emailProvider?: Api.EmailProviderType;
}

const isMicrosoftProvider = (emailProvider?: Api.EmailProviderType) =>
	emailProvider === 'Office365' || emailProvider === 'Outlook';

export const SharedMailboxSettingsSection: React.FC<IProps> = ({ emailProvider }) => {
	const isMicrosoft = isMicrosoftProvider(emailProvider);

	const { data, isLoading } = useSharedMailboxQuery({
		meta: {
			toastErrorMessage: 'Failed to load shared mailbox settings',
			errorLogging: { category: 'SharedMailboxSettings', action: 'Query-Error' },
		},
	});

	const history = useHistory();
	const location = useLocation();

	const [checked, setChecked] = React.useState(false);
	const [emailAddress, setEmailAddress] = React.useState('');
	const [name, setName] = React.useState('');
	const [validationError, setValidationError] = React.useState<string | null>(null);
	const [verifyStatus, setVerifyStatus] = React.useState<'idle' | 'validating' | 'success' | 'error'>('idle');
	const [verifyMessage, setVerifyMessage] = React.useState<string | null>(null);

	const syncFromSettings = React.useCallback((settings: Api.ISharedMailboxSettings) => {
		setChecked(settings.enabled);
		setEmailAddress(settings.emailAddress ?? '');
		setName(settings.name ?? '');
	}, []);

	// Seed the form from the server value once; later resets are explicit so in-flight typing isn't clobbered.
	const initializedRef = React.useRef(false);
	React.useEffect(() => {
		if (data && !initializedRef.current) {
			initializedRef.current = true;
			syncFromSettings(data);
		}
	}, [data, syncFromSettings]);

	const testMutation = useTestSharedMailboxMutation({
		onError: error => {
			setVerifyStatus('error');
			setVerifyMessage(error.systemMessage || 'We couldn’t verify the shared mailbox. Try again.');
		},
		onSuccess: () => setVerifyStatus('success'),
	});

	const runValidation = () => {
		setVerifyMessage(null);
		setVerifyStatus('validating');
		testMutation.mutate();
	};

	const updateMutation = useUpdateSharedMailboxMutation({
		onError: error => setValidationError(error.systemMessage || 'Failed to save shared mailbox.'),
		onSuccess: saved => {
			setValidationError(null);
			syncFromSettings(saved);
			toast.success('Shared mailbox saved.');
			if (saved.enabled && !saved.reconnectRequired) {
				runValidation();
			} else {
				setVerifyStatus('idle');
				setVerifyMessage(null);
			}
		},
	});

	const deleteMutation = useDeleteSharedMailboxMutation({
		onError: error => {
			// Roll back the optimistic uncheck — still enabled on the server.
			setChecked(data?.enabled ?? true);
			toast.error(error.systemMessage || 'Failed to turn off the shared mailbox.');
		},
		onSuccess: cleared => {
			syncFromSettings(cleared);
			setVerifyStatus('idle');
			setVerifyMessage(null);
			toast.success('Shared mailbox turned off.');
		},
	});

	// After a reconnect we return with `?validate=sharedMailbox`; validate once, then strip the flag.
	const autoValidatedRef = React.useRef(false);
	React.useEffect(() => {
		if (!data || autoValidatedRef.current) {
			return;
		}
		const params = new URLSearchParams(location.search);
		if (params.get('validate') === 'sharedMailbox' && data.enabled && !data.reconnectRequired) {
			autoValidatedRef.current = true;
			runValidation();
			history.replace(location.pathname);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, location.search]);

	const onToggleEnabled = (next: boolean | 'indeterminate') => {
		const enabled = next === true;
		setChecked(enabled);
		setValidationError(null);
		if (!enabled && data?.enabled) {
			deleteMutation.mutate();
		}
	};

	const onSave = () => {
		const trimmedAddress = emailAddress.trim();
		if (!trimmedAddress) {
			setValidationError('Enter the shared mailbox email address.');
			return;
		}
		setValidationError(null);
		updateMutation.mutate({ emailAddress: trimmedAddress, name: name.trim() || undefined });
	};

	if (isLoading || !data) {
		return null;
	}

	// Shared-mailbox sending only applies when Levitate sends through the user's own provider. When the account
	// sends through Levitate's hosted infrastructure there is no delegation, so don't offer the option at all.
	// (`available` is undefined on older API builds, so only an explicit `false` hides the section.)
	// See https://github.com/Real-Magic/Levitate/issues/19289
	if (data.available === false) {
		return null;
	}

	const reconnectRequired = isMicrosoft && data.enabled && data.reconnectRequired;
	// `reconnect=true` skips the new-connection wizard; `returnUrl` returns here and auto-runs verification.
	const reconnectHref = `${window.location.origin}/#/user/connect-email?reconnect=true&returnUrl=${encodeURIComponent(
		'/settings/personal/profile?validate=sharedMailbox'
	)}`;

	return (
		<div className={css(baseStyleSheet.mb2, styleSheet.container)}>
			<div className={css(styleSheet.enableRow)}>
				<Checkbox id='sharedMailboxEnabled' checked={checked} onCheckedChange={onToggleEnabled} />
				<label htmlFor='sharedMailboxEnabled' className={css(styleSheet.enableLabel)}>
					Send from a shared mailbox
				</label>
			</div>

			{checked && (
				<div className={css(styleSheet.fields)}>
					<div className={css(styleSheet.disclaimer)}>
						Note: You can connect a shared mailbox you have permission to send from. Distribution lists / distribution
						groups can’t be connected — they don’t have a mailbox to send from. Not sure? If you can set the address as
						your “From” in Outlook/Gmail, it’ll work here too.
					</div>
					<div className={css(styleSheet.field)}>
						<label htmlFor='sharedMailboxAddress' className={css(styleSheet.fieldLabel)}>
							Address
						</label>
						<Input
							id='sharedMailboxAddress'
							type='email'
							placeholder='team@company.com'
							value={emailAddress}
							onChange={e => setEmailAddress(e.target.value)}
							styles={[styleSheet.inputControl]}
						/>
					</div>
					<div className={css(styleSheet.field)}>
						<label htmlFor='sharedMailboxName' className={css(styleSheet.fieldLabel)}>
							Display name (optional)
						</label>
						<Input
							id='sharedMailboxName'
							type='text'
							placeholder='Team Inbox'
							value={name}
							onChange={e => setName(e.target.value)}
							styles={[styleSheet.inputControl]}
						/>
					</div>
					{validationError && <div className={css(styleSheet.validationError)}>{validationError}</div>}
					{!isMicrosoft && (
						<div className={css(styleSheet.gmailHint)}>
							The address must be configured as a “send-as” alias in your Google account.
						</div>
					)}
					<div className={css(styleSheet.actions)}>
						<Button
							variant='primary'
							size='sm'
							onClick={onSave}
							isLoading={updateMutation.isPending}
							styles={[styleSheet.actionButton]}
						>
							Save shared mailbox
						</Button>
					</div>

					{verifyStatus === 'validating' && (
						<div className={css(styleSheet.verifyStatus, styleSheet.verifyValidating)}>Validating shared mailbox…</div>
					)}
					{verifyStatus === 'success' && (
						<div className={css(styleSheet.verifyStatus, styleSheet.verifySuccess)}>
							✓ Verified — this mailbox can send. The test goes to a Levitate verification address (not your inbox), so
							you won’t receive a copy.
						</div>
					)}
					{verifyStatus === 'error' && (
						<div className={css(styleSheet.verifyStatus, styleSheet.verifyError)}>
							<span>✗ {verifyMessage}</span>
							<Button variant='link' size='sm' onClick={runValidation}>
								Try again
							</Button>
						</div>
					)}

					{reconnectRequired && (
						<div className={css(styleSheet.reconnectBanner)}>
							<span className={css(styleSheet.reconnectText)}>
								Reconnect Office 365 to grant permission to send from this mailbox, then we’ll verify it can send.
							</span>
							<Button variant='secondary' size='sm' asChild>
								<a href={reconnectHref}>Reconnect Office 365</a>
							</Button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
